import {CreateAccessor} from "mobx-graphlink";
import {SceneryKindDef} from "../../../Terrain/Scenery/@SceneryTypes.js";
import {SCENERY_RESOLVER_DEPTH_CAP, SceneryResolutionError, ResolvedSceneryKind, resolveSceneryKindFromChain} from "../../../Terrain/Scenery/SceneryResolverCore.js";
import {GetTerrainSceneriesMods} from "./$mapObject.js";
import {builtInSceneryKinds} from "../../../Terrain/Scenery/SceneryKindRegistry.js";

/** All user-authored scenery kinds map-wide (deduped by id) — the union of `userKinds` on every
 *  enabled `Mod_TerrainSceneries` mod, plus the built-ins. The catalogue is the union of the
 *  built-in registry + these. Duplicate ids resolve to the same entry. Set13 §4.11. */
export const GetProcSceneryKinds = CreateAccessor((mapID: string): SceneryKindDef[]=>{
	const result = [
		...builtInSceneryKinds.values(),
	];
	for (const mod of GetTerrainSceneriesMods(mapID)) {
		for (const k of mod.userKinds) result.push(k);
	}
	return result;
});
export const GetProcSceneryKindsByID = CreateAccessor((mapID: string): Map<string, SceneryKindDef>=>{
	const result = GetProcSceneryKinds(mapID);
	return result.ToMap(a=>a.id, a=>a);
});

export const GetProcSceneryKind = CreateAccessor((kindID: string|n, mapID: string): SceneryKindDef | undefined=>{
	return GetProcSceneryKindsByID(mapID).get(kindID!);
});

/** Resolve a kind id to a flat ResolvedSceneryKind. Throws SceneryResolutionError on unknown id,
 *  cycle, or depth-cap overflow; emits warnings (not errors) for overrides naming ids absent from
 *  the resolved params (the expected symptom of base drift). */
export const ResolveSceneryKind = CreateAccessor((id: string, mapID: string): ResolvedSceneryKind=>{
	// Walk the base chain root-ward, tracking cycles + depth.
	const chain: SceneryKindDef[] = [];
	const visited = new Set<string>();
	let cur = GetProcSceneryKind(id, mapID);
	if (cur == null) throw new SceneryResolutionError(`scenery kind "${id}" not found in catalogue`);
	while (cur != null) {
		if (visited.has(cur.id)) throw new SceneryResolutionError(`cycle detected: ${[...visited, cur.id].join(" -> ")}`);
		visited.add(cur.id);
		if (visited.size > SCENERY_RESOLVER_DEPTH_CAP) throw new SceneryResolutionError(`depth cap (${SCENERY_RESOLVER_DEPTH_CAP}) exceeded: ${[...visited].join(" -> ")}`);
		chain.unshift(cur);
		if (cur.baseKindID == null) break;
		cur = GetProcSceneryKind(cur.baseKindID, mapID);
	}
	return resolveSceneryKindFromChain(chain);
});

/** Resolve a SET of kind ids (order preserved). Each id resolves through the per-id memo, so a
 *  set resolve only pays for uncached members. */
export function ResolveSceneryKindSet(ids: string[], mapID: string): ResolvedSceneryKind[] {
	return ids.map(id=>ResolveSceneryKind(id, mapID));
}

/** Resolve the lineage of a kind (root → leaf) for the lineage display + cost model.  Returns
 *  the chain + the resolved kind; warnings are surfaced (base drift).  Mirrors
 *  `GetProcMatRecipeLineage` for materials. */
export function GetSceneryKindLineage(id: string, mapID: string): {chain: string[]; resolved: ResolvedSceneryKind | null; error: string | null} {
	const ids: string[] = [];
	let cur = id;
	try {
		for (let i = 0; i < SCENERY_RESOLVER_DEPTH_CAP + 1; i++) {
			ids.push(cur);
			const k = GetProcSceneryKind(cur, mapID);
			if (k == null) {
				return {chain: ids, resolved: null, error: `scenery kind "${cur}" not found`};
			}
			if (k.baseKindID == null) break;
			cur = k.baseKindID;
		}
		const resolved = ResolveSceneryKind(id, mapID);
		return {chain: ids, resolved, error: null};
	} catch (err) {
		return {chain: ids, resolved: null, error: (err as Error).message};
	}
}
